import { useContext } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { ArcadeBorderedCard, ArcadeButton, Flex, spacing, Text } from '@cash-design-system/react'
import { FormBlockerElementCallToActionElement } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import logger from '@cash-web/shared/util-logger'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useTextTokenizer } from '@cash-web/shared-feature-use-text-tokenizer'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

const MaxWidthImage = styled.img`
  max-width: 100%;
`

const ArcadeWrapper = styled(ArcadeBorderedCard)<{ $compact: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${({ theme, $compact }) => ($compact ? `${theme.spacing.m} ${theme.spacing.l}` : theme.spacing.l)};
  margin: ${({ theme: { spacing } }) => `${spacing.empty} ${spacing.xs}`};
`

const ArcadeHeader = styled(Text)`
  margin: ${({ theme: { spacing } }) => `${spacing.empty} ${spacing.xxs}`};
`

const ArcadeSubtitle = styled(Text)`
  margin: ${({ theme: { spacing } }) => `${spacing.empty} ${spacing.xxs}`};
`

const ArcadeEyebrow = styled(Text)`
  text-transform: uppercase;
  margin-bottom: ${spacing.s};
`

export const ArcadeActionButton = styled(ArcadeButton)`
  margin: ${({ theme: { spacing } }) => `${spacing.l} ${spacing.xxs} ${spacing.xs} ${spacing.xxs}`};
`

const CompactActionButton = styled(ArcadeButton)`
  align-self: flex-start;
  margin-top: ${spacing.m};
`

// NOTE: The `accent_color` property is purposefully ignored: https://cash.slack.com/archives/C01M5JHQBCM/p1712264532738299
export const CallToActionElement = (props: FormBlockerElementCallToActionElement) => {
  const { setError } = useContext(ErrorContext)
  const { t } = useTranslation()
  const { darkMode } = useTheme()

  const flow = useFlowContext()

  const textTokenizer = useTextTokenizer({ textLink: { target: '_blank' } })

  const handleClick = () => {
    if (props?.button_action) {
      flow.executeBlockerAction(props?.button_action)
    } else {
      setError(t('This action is not currently supported', { context: 'Error text.' }))
      logger.error(new Error('Missing blocker action in CallToAction element'))
    }
  }

  // NOTE: The image_width and image_height properties are ignored. They are also ignored in iOS.
  const imageUrl = darkMode ? props.image?.dark_url : props.image?.light_url

  const tokenizedTitle = textTokenizer(props.title)
  const tokenizedSubtitle = textTokenizer(props.subtitle)
  const compact = props.compact_presentation != null
  const eyebrow = props.compact_presentation?.eyebrow?.translated_value

  return (
    <ArcadeWrapper $compact={compact}>
      <Flex flexDirection="column" gap={spacing.l}>
        {imageUrl && (
          <Flex justifyContent="center" alignItems="center">
            <MaxWidthImage src={imageUrl} alt="" />
          </Flex>
        )}
        <Flex flexDirection="column" gap={spacing.s}>
          {eyebrow && (
            <ArcadeEyebrow data-testid="calltoaction-eyebrow" typography="labelXSmall" color="textStandard">
              {eyebrow}
            </ArcadeEyebrow>
          )}
          {props.title && (
            <ArcadeHeader
              data-testid="calltoaction-title"
              as={compact ? 'h2' : 'h1'}
              typography={compact ? 'headlineSmall' : 'header'}
              color="textStandard"
            >
              {tokenizedTitle}
            </ArcadeHeader>
          )}
          {props.subtitle && (
            <ArcadeSubtitle
              data-testid="calltoaction-subtitle"
              color={compact && props.title ? 'textSubtle' : 'textStandard'}
              typography="body"
            >
              {tokenizedSubtitle}
            </ArcadeSubtitle>
          )}
          {props.button_action && compact && (
            <CompactActionButton
              data-testid="calltoaction-arcadebutton"
              size="compact"
              prominence="standard"
              type="button"
              onClick={handleClick}
            >
              {props.button_action.text}
            </CompactActionButton>
          )}
          {props.button_action && !compact && (
            <ArcadeActionButton
              data-testid="calltoaction-arcadebutton"
              size="cta"
              prominence="standard"
              type="button"
              onClick={handleClick}
            >
              {props?.button_action?.text}
            </ArcadeActionButton>
          )}
        </Flex>
      </Flex>
    </ArcadeWrapper>
  )
}

export default CallToActionElement
